import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Inject,
  OnInit,
} from '@angular/core';
import { AsyncPipe, DatePipe } from '@angular/common';
import {
  CdkFixedSizeVirtualScroll,
  CdkVirtualForOf,
  CdkVirtualScrollViewport,
} from '@angular/cdk/scrolling';
import {
  TuiButtonModule,
  TuiDialogContext,
  TuiScrollbarModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import {
  TuiCheckboxModule,
  TuiInputModule,
  TuiLineClampModule,
} from '@taiga-ui/kit';
import { TuiTableModule } from '@taiga-ui/addon-table';
import {
  BehaviorSubject,
  map,
  shareReplay,
  Subject,
  tap,
  withLatestFrom,
  combineLatest,
  Observable,
} from 'rxjs';
import {
  POLYMORPHEUS_CONTEXT,
  PolymorpheusComponent,
  PolymorpheusModule,
} from '@tinkoff/ng-polymorpheus';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { fromFormControlValue } from '@shared/utils/observables';

export type ItemsSelectInputData<T> = {
  required?: boolean;
  submitText?: string;
  items: T[];
  identity: keyof T;
  headers: Array<{
    name: string;
    value: keyof T;
    width?: string;
    isDate?: boolean;
    component?: PolymorpheusComponent<any>;
  }>;
  title: string;
  showSearch?: boolean;
  comparator?: (search: string, item: T) => boolean;
};
@Component({
  selector: 'app-items-select',
  standalone: true,
  imports: [
    AsyncPipe,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    CdkVirtualScrollViewport,
    TuiButtonModule,
    TuiCheckboxModule,
    TuiScrollbarModule,
    TuiTableModule,
    FormsModule,
    TuiLineClampModule,
    DatePipe,
    TuiInputModule,
    ReactiveFormsModule,
    TuiTextfieldControllerModule,
    PolymorpheusModule,
  ],
  templateUrl: './items-select.component.html',
  styleUrl: './items-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemsSelectComponent<T> implements OnInit {
  title$ = new BehaviorSubject<string>('');
  items$: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
  submitText$ = new BehaviorSubject('Применить');
  selectAll$ = new Subject<void>();
  selectedItems$ = new BehaviorSubject<T[]>([]);
  required$ = new BehaviorSubject(true);
  selectedItemsIds$ = new BehaviorSubject<Array<T[keyof T]>>([]);

  disabled$ = combineLatest([this.required$, this.selectedItems$]).pipe(
    map(([required, items]) => {
      if (required) {
        return !items.length;
      }
      return false;
    }),
  );

  search = this.fb.control('');

  filteredItems$: Observable<T[]> = combineLatest([
    fromFormControlValue(this.search),
    this.items$,
  ]).pipe(
    map(([search, items]) => {
      if (!search) {
        return items;
      }
      const comparator = this.comparator;
      return comparator !== null
        ? items.filter(item => comparator(search, item))
        : items;
    }),
  );

  isAllSelected$ = combineLatest([
    this.selectedItemsIds$,
    this.filteredItems$,
  ]).pipe(
    map(([selected, items]) => selected.length === items.length),
    shareReplay(),
  );

  constructor(
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<T[], ItemsSelectInputData<T>>,
    private readonly destroyRef: DestroyRef,
    private readonly cdr: ChangeDetectorRef,
    private readonly fb: FormBuilder,
  ) {
    this.title$.next(context.data.title);
    this.submitText$.next(context.data.submitText ?? 'Применить');
    this.required$.next(context.data?.required ?? true);
  }

  onSelect(item: T) {
    const id = item[this.identity];
    if (this.selectedItemsIds$.value.includes(id)) {
      const filteredItems = this.selectedItems$.value.filter(
        item => item[this.identity] !== id,
      );
      const filteredIds = this.selectedItemsIds$.value.filter(v => v !== id);
      this.selectedItemsIds$.next(filteredIds);
      this.selectedItems$.next(filteredItems);
    } else {
      this.selectedItemsIds$.next([...this.selectedItemsIds$.value, id]);
      this.selectedItems$.next([...this.selectedItems$.value, item]);
    }
  }

  onAllSelectClick() {
    this.selectAll$
      .pipe(
        withLatestFrom(this.filteredItems$, this.isAllSelected$),
        tap(([, items, allSelected]) => {
          if (allSelected) {
            this.selectedItems$.next([]);
            this.selectedItemsIds$.next([]);
          } else {
            const ids = items.map(item => item[this.identity]);
            this.selectedItemsIds$.next(ids);
            this.selectedItems$.next(items);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  get comparator(): Nullable<(s: string, i: T) => boolean> {
    return this.context.data.comparator ?? null;
  }

  get headers() {
    return this.context.data.headers;
  }

  get identity() {
    return this.context.data.identity;
  }

  get showSearch() {
    return this.context.data?.showSearch ?? false;
  }

  stringify(value: keyof T) {
    return value.toString();
  }

  ngOnInit() {
    this.onAllSelectClick();
    this.items$.next(this.context.data.items);
  }

  isSelected(item: T) {
    return this.selectedItemsIds$.value.includes(item[this.identity]);
  }

  close() {
    this.context.$implicit.complete();
  }

  submit() {
    this.context.$implicit.next(this.selectedItems$.value);
    this.context.$implicit.complete();
  }

  get columns() {
    const cols = this.headers.map(c => c.value);
    return ['selected', ...cols];
  }
}
